import {useMemo, useRef, useState} from 'react';
import { Box, Grid2 as Grid, TableContainer, Table, TableHead, TableRow, TableCell, useMediaQuery, useTheme, Paper, Typography, TableBody, Stack, styled, TableSortLabel, debounce, FormControl, Chip, TextField, TablePagination} from '@mui/material';
import { ReceiptItem } from '../../../types/reports/receiptItem';
import { visuallyHidden } from '@mui/utils';

interface Props {
   tableRows?:ReceiptItem[];
   hideType?: boolean;
   filerName?: string;
}

const ROWS_PER_PAGE = 100;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

const ReceiptsScheduleTable = (props: Props): JSX.Element => {
    
    const theme = useTheme();
    const [receiptRows, setReceiptRows] = useState<ReceiptItem[]>(props.tableRows ?? []);
    const [allRows, setAllRows] = useState<ReceiptItem[]>(props.tableRows ?? [])
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState<'desc' | 'asc'>('asc');
    const [page, setPage] = useState(0);
    const tableTopRef = useRef<HTMLDivElement>(null);
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const pagedRows = useMemo(
        () => receiptRows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE),
        [receiptRows, page]
    );

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        tableTopRef.current?.scrollIntoView({ block: 'start' });
    }

    const sortByAmount = () => {
        const sortedRows = [...receiptRows].sort((a, b) => {
            if (order === 'asc') {
                return b.amountValue - a.amountValue;
            } else {
                return a.amountValue - b.amountValue;
            }
        });
        setReceiptRows(sortedRows);
        setPage(0);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const sortByDate = () => {
        const sortedRows = [...receiptRows].sort((a, b) => {
            if (order === 'asc') {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            } else {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }
        });
        setReceiptRows(sortedRows);
        setPage(0);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const sortByName = () => {
        const sortedRows = [...receiptRows].sort((a, b) => {
            if (order === 'asc') {
                return b.fullNameReverse.localeCompare(a.fullNameReverse);
            } else {
                return a.fullNameReverse.localeCompare(b.fullNameReverse);
            }
        });
        setReceiptRows(sortedRows);
        setPage(0);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const sortByEmployer = () => {
        const sortedRows = [...receiptRows].sort((a, b) => {
            if (order === 'asc') {
                return b.employer.localeCompare(a.employer);
            } else {
                return a.employer.localeCompare(b.employer);
            }
        });
        setReceiptRows(sortedRows);
        setPage(0);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const updateSort = (column: string) => {
        setOrderBy(column);
    }

    const pagination = receiptRows.length > ROWS_PER_PAGE &&
        <TablePagination
            component={'div'}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            count={receiptRows.length}
            rowsPerPage={ROWS_PER_PAGE}
            page={page}
            onPageChange={handleChangePage}
            showFirstButton
            showLastButton
        />;

    const filterByName = useMemo(() => debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        if(searchInput.length === 0){
          setReceiptRows(allRows);
        }
        else{
          let filteredRows = allRows?.filter(
            (row) => (row.fullNameReverse.toLowerCase().includes(searchInput) 
            || row.fullAddress.toLowerCase().includes(searchInput) 
            || row.tenderTypeDescription?.toLowerCase().includes(searchInput) 
            || row.description.toLowerCase().includes(searchInput) 
            || row.occupation?.toLowerCase().includes(searchInput) 
            || row.employer?.toLowerCase().includes(searchInput) 
            || row.principalOfficer?.toLowerCase().includes(searchInput) 
            )
        ) || [];
          setReceiptRows(filteredRows);
        }
        setPage(0);
      }, 800), [allRows]);


    const isOutOfPocketRelated = (recordTypeId: number) => {
        return recordTypeId === 331 || recordTypeId === 332;
    }

   /* NOT USE MUI DATAGRID
    return (
        <Box 
            sx={{ 
                width: '100%',
                '.MuiDataGrid-columnHeaderTitle': { 
                    fontWeight: 'bold !important',
                    whiteSpace: 'normal !important',
                },
         }}>
        <DataGrid 
            rows={props.tableRows} 
            columns={tableColumns}
            columnVisibilityModel={{
                tenderTypeDescription: hideType,
                amount: isSM? true: false,
                amountDisplay: isSM? false: true,
            }}
            getRowHeight={() => 'auto'}
            getRowId={(row) => row.id}
            hideFooterPagination={totalCount < 100? true: false}
            disableColumnMenu
            slots={{ toolbar: QuickSearchToolbar }}
            sx={{ 
                '.MuiDataGrid-cell': {
                  alignItems: 'center', 
                  wordBreak: 'break-word !important',
                  //whiteSpace: 'normal !important',
                  paddingTop: '3px!important',
                  paddingBottom: '3px!important',
                },
                '.MuiDataGrid-row--dynamicHeight>.MuiDataGrid-cell':{
                    whiteSpace: 'break-spaces !important',
                    lineHeight: 'normal !important',
                }
              }}
        />
        </Box>
    );
    */

    return (
        <Grid container spacing={1} marginTop={1} marginBottom={1}>
         <Grid size={12} marginBottom={1} ref={tableTopRef}>
           <TextField id="search-input" label="Search..." variant="outlined" onChange={filterByName} size="small" />
         </Grid>
         <Grid size={12}>
           {pagination}
         </Grid>
         <TableContainer component={Paper}>
             <Table aria-label="receipts table" size="small">
                 <TableHead>
                     <TableRow>
                         <TableCell onClick={sortByDate} onMouseEnter={()=>updateSort('date')}>
                            <Typography
                                variant={'subtitle2'}
                                fontWeight={600}
                                className='is-clickable'>
                                Date
                            </Typography>
                         </TableCell>
                         <TableCell onClick={sortByName} onMouseEnter={()=>updateSort('name')}>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={sortByName}
                            >
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    className='is-clickable' 
                                 >
                                    Name/Address
                                </Typography>
                                {orderBy === 'name' ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                         </TableCell>
                         {
                            isSM &&
                            <TableCell align='right' onClick={sortByAmount} onMouseEnter={()=>updateSort('amount')}>
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    className='is-clickable' 
                                    >
                                    Amount
                                </Typography>
                            </TableCell>
                         }
                         <TableCell>
                             <Typography
                             variant={'subtitle2'}
                             fontWeight={600}>
                                Type
                             </Typography>
                         </TableCell>
                         <TableCell onClick={sortByEmployer} onMouseEnter={()=>updateSort('employer')}>
                             <Typography
                                variant={'subtitle2'}
                                fontWeight={600}
                                className='is-clickable' 
                                >
                                Occupation/Employer
                             </Typography>
                         </TableCell>
                         <TableCell>
                             <Typography
                             variant={'subtitle2'}
                             fontWeight={600}>
                                Other
                             </Typography>
                         </TableCell>
                         {
                            !isSM &&
                            <TableCell onClick={sortByAmount} align='right' onMouseEnter={()=>updateSort('amount')}>
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    className='is-clickable' 
                                    >
                                     Amount
                                </Typography>
                            </TableCell>
                         }
                     </TableRow>
                 </TableHead>
                 <TableBody>
                     {pagedRows.map((receipt) => (
                         <StyledTableRow key={receipt.id}>
                             <TableCell>
                                 <Typography variant={'body2'}>
                                     {receipt.date}
                                 </Typography>
                                 {
                                    receipt.isPreviousYearContribution && 
                                    <Chip sx={{marginTop: 0.5}} label={new Date(receipt.date).getFullYear()-1 + ' contribution'}  color="primary" variant='outlined' size="small" />
                                 }
                             </TableCell>
                             <TableCell>
                                 <Typography variant={'body2'} fontWeight={550}>
                                     {isOutOfPocketRelated(receipt.recordTypeId)?props.filerName: receipt.fullNameReverse} 
                                 </Typography>
                                 <Typography variant={'body2'} component={'i'}>
                                     {isOutOfPocketRelated(receipt.recordTypeId)?'':receipt.fullAddress}
                                 </Typography>
                             </TableCell>
                             {
                                isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {receipt.recordTypeId === 319 ? `(${receipt.amount})`: receipt.amount}
                                    </Typography>
                                </TableCell>
                             }
                             <TableCell>
                                 <Typography variant={'body2'}>
                                     {receipt.recordTypeDescription}
                                 </Typography>
                             </TableCell>
                             <TableCell>
                                 {
                                    receipt.principalOfficer?.trim() ?
                                        <Stack direction={'column'}>
                                            <Typography variant="body2">{receipt.principalOfficer} (Principal Officer)</Typography>
                                        </Stack>:
                                        <Stack>
                                            <Typography variant="body2">{receipt.occupation}</Typography>
                                            <Typography variant="body2"><i>{receipt.employer}</i></Typography>
                                        </Stack>
                                 }
                             </TableCell>
                             <TableCell>
                                <Stack>
                                    <Typography variant="body2">{receipt.tenderTypeDescription}</Typography>
                                    <Typography variant="body2">{receipt.description}</Typography>
                                    {
                                        typeof receipt.bqQuestionNumber === 'number' && receipt.bqQuestionNumber > 0 &&
                                        <Typography variant="body2">(for question {receipt.bqQuestionNumber})</Typography>
                                    }
                                    <Typography variant="body2">{receipt.relatedCpfId}</Typography>
                                </Stack>  
                             </TableCell>
                             {
                                !isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {receipt.recordTypeId === 319 ? `(${receipt.amount})`: receipt.amount}
                                    </Typography>
                                </TableCell>
                             }   
                         </StyledTableRow>
                    ))}
                 </TableBody>
             </Table>
             {pagination}
         </TableContainer>
        </Grid>
    );
}

export default ReceiptsScheduleTable;